/* eslint-disable react/jsx-props-no-spreading */
import {
    createInitialNumberState,
    IventisTableData,
    RowNetworkStatus,
    TimeCellEditor,
    TimeCellEditorParams,
    IventisCellEditor,
    IventisCellEditorParams,
    IventisTimeCellData,
    IventisRowNode,
} from "@iventis/data-table";
import React, { BaseSyntheticEvent, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { DataTableTheme, narrowPadding } from "@iventis/styles";
import { useTheme, Input } from "@mui/material";
import { addMinutes } from "date-fns";
import { ScheduleTableRowNode } from "./schedule-table.types";
import { getDisplayTime } from "./run-sheet-helpers";

export type DurationCellEditorRef = IventisCellEditor & {
    /** Passing true means the cell editor will not perform the cell edit and let the parent do it instead. It will just provide the new value from the input */
    getValue(justValue?: boolean): number;
};

const durationToastkey = "DURATION_NOTIFICATION" as const;

export const DurationCellEditor = forwardRef<DurationCellEditorRef, IventisCellEditorParams<IventisTimeCellData>>((props, ref) => {
    const { node, column, value: propValue, validator, required, context } = props;
    const initialState = createInitialNumberState(props);
    const { data } = node as IventisRowNode<IventisTimeCellData>;
    const [value, setValue] = useState<number>(Number(initialState.value));
    const refInput = useRef(null);

    const endAt = useMemo(() => addMinutes(data.startAt.value, value), [value]);

    const { doesFail, errorMessage } = validator(node, column.getColId() as keyof IventisTableData, value);

    const theme: DataTableTheme = useTheme();

    useEffect(() => {
        if (doesFail) {
            context.toast.error({ title: context.translate("Error"), message: errorMessage }, { key: durationToastkey });
        }
    }, [errorMessage]);

    useImperativeHandle(ref, () => ({
        getValue(preventDefaultSideEffects?: boolean) {
            if (preventDefaultSideEffects) {
                // If preventing side effects, just return the value without any other computation
                return value;
            }
            props.api.getRowNode(node.id).setDataValue("endAt", { displayText: getDisplayTime(endAt, context.timezone), value: endAt });
            return value;
        },
        isCancelAfterEnd() {
            return doesFail || value === propValue;
        },
        isCancelBeforeStart() {
            // If the field is not required or has a value, while we are pending creation, make this cell uneditable
            const blockEditsUntilCreatedOnServer = (!required || propValue != null) && node.data.networkStatus === RowNetworkStatus.PendingCreation;
            return !data.canEdit || blockEditsUntilCreatedOnServer;
        },
    }));

    return (
        <Input
            type="number"
            style={{ height: "100%", paddingLeft: narrowPadding, backgroundColor: theme.dataTableTheme.background }}
            ref={refInput}
            value={value}
            onChange={(event: BaseSyntheticEvent) => setValue(event.target.value)}
            error={doesFail}
            autoFocus
        />
    );
});

export const ScheduleTableTimeCellEditor = forwardRef<IventisCellEditor, TimeCellEditorParams>((props, ref) => {
    const { data } = props.node as ScheduleTableRowNode;
    const timeCellRef = useRef(null);

    useImperativeHandle(ref, () => ({
        getValue() {
            return timeCellRef.current?.getValue();
        },
        isCancelAfterEnd() {
            return timeCellRef.current?.isCancelAfterEnd();
        },
        isCancelBeforeStart() {
            if (data.relativeEventRules?.length > 0) {
                return true;
            }
            return timeCellRef.current?.isCancelBeforeStart();
        },
        isPopup() {
            return timeCellRef.current?.isPopup();
        },
    }));

    return <TimeCellEditor ref={timeCellRef} {...props} />;
});

export const ScheduleTableDurationCellEditor = forwardRef<IventisCellEditor, TimeCellEditorParams<IventisTimeCellData>>((props, ref) => {
    const { data } = props.node as ScheduleTableRowNode;
    const durationCellEditor = useRef(null);

    useImperativeHandle(ref, () => ({
        getValue() {
            return durationCellEditor.current?.getValue();
        },
        isCancelAfterEnd() {
            return durationCellEditor.current?.isCancelAfterEnd();
        },
        isCancelBeforeStart() {
            if (data.relativeEventRules?.length > 0) {
                return true;
            }
            return durationCellEditor.current?.isCancelBeforeStart();
        },
    }));

    return <DurationCellEditor ref={durationCellEditor} {...props} />;
});
